import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  clearErrors,
  setLoading,
  signInWithGoogle,
  signUp,
} from '../../../store/auth';
import { useAppDispatch as useDispatch } from '../../../store/store';
import { RootState } from 'store/store';
import errors from '../../../services/errors';

import facebookSvg from 'images/Facebook.svg';
import twitterSvg from 'images/Twitter.svg';
import googleSvg from 'images/Google.svg';

import Input from 'components/Input/Input';
import ButtonPrimary from 'components/Button/ButtonPrimary';
import NcLink from 'components/NcLink/NcLink';
import Heading2 from 'components/Heading/Heading2';
import Image from 'components/Image';
import Layout from '../layout';

const loginSocials = [
  {
    name: 'Continue with Facebook',
    href: '#',
    icon: facebookSvg,
  },
  {
    name: 'Continue with Twitter',
    href: '#',
    icon: twitterSvg,
  },
  {
    name: 'Continue with Google',
    href: '#',
    icon: googleSvg,
  },
];

const PageSignUp = () => {
  const [isBusinessAccount, setIsBusinessAccount] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(clearErrors());
      dispatch(setLoading(false));
    };
  }, [dispatch]);

  const handleGoogleSignIn = async () => {
    const response = await dispatch(signInWithGoogle());
    console.log(`Handle google sign in response: ${response}`);
    // Navigate to home page if success
    if (response) {
      navigate('/');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Signing up...');
    const response = await dispatch(
      signUp({
        email,
        password,
        firstName,
        lastName,
        isBusinessAccount,
        companyName,
      })
    );
    // Navigate to home page if success
    if (response) {
      navigate('/');
    }
  };

  const { errors: authErrors } = useSelector((state: RootState) => state.auth);

  return (
    <Layout>
      <header className="text-center max-w-2xl mx-auto - mb-14 sm:mb-16 lg:mb-20">
        <Heading2>Sign up</Heading2>
        <span className="block text-sm mt-2 text-neutral-700 sm:text-base dark:text-neutral-200">
          Welcome to our Orakly Community
        </span>
      </header>

      <div className="max-w-md mx-auto space-y-6">
        <div className="grid gap-3">
          {loginSocials.map((item, index) => (
            <a
              key={index}
              href={item.href}
              onClick={handleGoogleSignIn}
              className=" flex w-full rounded-lg bg-primary-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
            >
              <Image
                className="flex-shrink-0"
                src={item.icon}
                alt={item.name}
              />
              <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                {item.name}
              </h3>
            </a>
          ))}
        </div>
        {/* OR */}
        <div className="relative text-center">
          <span className="relative z-10 inline-block px-4 font-medium text-sm bg-white dark:text-neutral-400 dark:bg-neutral-900">
            OR
          </span>
          <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-100 dark:border-neutral-800"></div>
        </div>

        {/* ERROR */}
        {authErrors && (
          <div className="text-center text-neutral-700 dark:text-neutral-300">
            {errors(authErrors)}
          </div>
        )}

        {/* FORM */}
        <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit}>
          <label className="flex items-center gap-1.5 -mt-3">
            <Input
              type="checkbox"
              className="border-neutral-300"
              sizeClass="h-6 w-6 rounded-full"
              checked={isBusinessAccount}
              onChange={(e) => setIsBusinessAccount(e.target.checked)}
            />
            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
              Business Account
            </span>
          </label>
          {isBusinessAccount && (
            <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                Company Name
              </span>
              <Input
                type="text"
                className="mt-1"
                placeholder="Orakly"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </label>
          )}
          <label className="block">
            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
              First Name
            </span>
            <Input
              type="text"
              className="mt-1"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
              Last Name
            </span>
            <Input
              type="text"
              className="mt-1"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-neutral-800 dark:text-neutral-200">
              Email address
            </span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
              className="mt-1"
            />
          </label>
          <label className="block">
            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
              Password
            </span>
            <Input
              type="password"
              className="mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <ButtonPrimary type="submit">Continue</ButtonPrimary>
        </form>

        {/* ==== */}
        <span className="block text-center text-neutral-700 dark:text-neutral-300">
          Already have an account? {` `}
          <NcLink href="/login">Sign in</NcLink>
        </span>
      </div>
    </Layout>
  );
};

export default PageSignUp;
